import { deviceNotify, dismissError, initApp } from 'actions/app'
import classnames from 'classnames'
import AppError from 'components/app-error'
import AppRouting from 'components/app/app-routing'
import styles from 'components/app/default-app.css'
import GlobalMessage from 'components/app/global-message'
import Loader from 'components/loader'
import Login from 'components/login'
import Register from 'components/register'
import { requestChannelViewMode } from 'libs/channels'
import { Component } from 'preact'
import { Router } from 'preact-router'
import { connect } from 'react-redux'
import GTM from 'react-tag-manager'
import { getP, setLanguage } from 'redux-polyglot'
import Settings from 'routes/settings'
import {
  getAvailableLanguages,
  getDefaultRoute,
  getErrorMessage,
  getGlobalMessage,
  getTagManagerId,
  getToast,
  getTranslationsReady,
  hasError,
  isLoggedIn,
  notifyIsReady,
  selectForceLogin,
  settingsIsReady
} from 'selectors/app'

class DefaultApp extends Component {
  constructor(props) {
    super(props)
    props.setLanguage('en', {})
    this.deviceNotifyInterval = null
    this.previousWheelTimestamp = 0
    this.previousWheelDirection = ''
    this.currentFocusedElement = null
  }

  componentDidMount() {
    this.props.initApp()
    this.deviceNotifyInterval = setInterval(() => {
      this.props.deviceNotify(false)
    }, 300000)

    this.updateDefaultFontSize()
    window.addEventListener('resize', this.updateDefaultFontSize)
    requestChannelViewMode('')
  }

  componentDidUpdate(prevProps) {
    if (
      (!this.props.settingsIsReady &&
        this.props.settingsIsReady !== prevProps.settingsIsReady) ||
      (this.props.settingsIsReady &&
        !this.props.notifyIsReady &&
        this.props.notifyIsReady !== prevProps.notifyIsReady)
    ) {
      this.props.initApp()
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDefaultFontSize)
  }

  getChildContext() {
    return {
      getFocusedElement: this.getFocusedElement,
      setFocusedElement: this.setFocusedElement
    }
  }

  getFocusedElement = _ => this.currentFocusedElement

  setFocusedElement = el => {
    this.currentFocusedElement = el
  }

  updateDefaultFontSize = () => {
    let fontSize
    if (
      document.documentElement.clientWidth /
        document.documentElement.clientHeight <
      16 / 9
    ) {
      fontSize = Math.floor(document.documentElement.clientWidth / 80)
    } else {
      fontSize = Math.floor(document.documentElement.clientHeight / 45)
    }
    document.getElementsByTagName('html')[0].style.fontSize = `${(fontSize *
      100) /
      16}%`
  }

  shouldDisplayGlobalMessage = () => {
    if (this.props.globalMessage === null) {
      return false
    }
    if (
      this.props.globalMessage.lastDisplayed === null ||
      (this.props.globalMessage.lastDisplayed > Date.now() - 24 * 3600 * 1000 &&
        this.props.globalMessage.lastDisplayed >
          this.props.globalMessage.lastModified)
    ) {
      return false
    }

    return true
  }

  handleWheel = event => {
    event.preventDefault()
    event.stopPropagation()

    const wheelTimestamp = Date.now()
    const wheelDirection = event.wheelDelta >= 0 ? 'UP' : 'DOWN'

    let triggerKeyDown = false
    if (wheelDirection !== this.previousWheelDirection) {
      triggerKeyDown = true
      this.previousWheelDirection = wheelDirection
      this.previousWheelTimestamp = wheelTimestamp
    } else {
      if (wheelTimestamp - this.previousWheelTimestamp > 350) {
        triggerKeyDown = true
        this.previousWheelTimestamp = wheelTimestamp
      }
    }

    if (triggerKeyDown) {
      if (document.createEventObject) {
        const newEvent = document.createEventObject()
        if (wheelDirection === 'UP') {
          newEvent.keyCode = 0x26
        } else {
          newEvent.keyCode = 0x28
        }
        document.activeElement &&
          document.activeElement.fireEvent('onkeydown', newEvent)
      } else if (document.createEvent) {
        const newEvent = document.createEvent('Events')
        if (wheelDirection === 'UP') {
          newEvent.initEvent('keydown', true, true)
          newEvent.which = 0x26
          newEvent.keyCode = 0x26
          newEvent.key = 'ArrowUp'
        } else {
          newEvent.initEvent('keydown', true, true)
          newEvent.which = 0x28
          newEvent.keyCode = 0x28
          newEvent.key = 'ArrowDown'
        }
        document.activeElement && document.activeElement.dispatchEvent(newEvent)
      }
    }
  }

  render({ polyglot: { t } }) {
    let body
    if (this.props.hasError) {
      body = (
        <AppError
          message={this.props.errorMessage}
          onEnter={() => this.props.dismissError()}
        />
      )
    } else if (!this.props.translationsReady) {
      body = <Loader message="" />
    } else if (!this.props.settingsIsReady) {
      body = (
        <Loader
          message={t('tv_app.retrieving_application_settings', {
            _: 'Retrieving application settings ...'
          })}
        />
      )
    } else if (!this.props.notifyIsReady) {
      body = (
        <Loader
          message={t('tv_app.registering_device', {
            _: 'Registering device ...'
          })}
        />
      )
    } else if (this.shouldDisplayGlobalMessage()) {
      body = (
        <GlobalMessage
          title={this.props.globalMessage.title}
          message={this.props.globalMessage.body}
          onEnter={null}
        />
      )
    } else if (this.props.forceLogin && !this.props.isLoggedIn) {
      body = (
        <Router>
          <Login path={`${__webpack_public_path__ || '/'}auth/login`} default />
          <Register path={`${__webpack_public_path__ || '/'}auth/register`} />
          <Settings
            path={`${__webpack_public_path__ || '/'}settings/:action?`}
          />
        </Router>
      )
    } else {
      body = [
        <AppRouting defaultRoute={this.props.defaultRoute} />,
        this.props.toast ? (
          <div
            class={classnames(styles.toast, {
              [styles.toastSuccess]: this.props.toast.type === 'success',
              [styles.toastError]: this.props.toast.type === 'error',
              [styles.toastWarning]: this.props.toast.type === 'warning'
            })}>
            {this.props.toast.message}
          </div>
        ) : null
      ]
    }

    if (this.props.tagManagerId !== '') {
      return (
        <div class={styles.rootComponent} onWheel={this.handleWheel}>
          <GTM
            gtm={{
              id: this.props.tagManagerId
            }}>
            {body}
          </GTM>
        </div>
      )
    } else {
      return (
        <div class={styles.rootComponent} onWheel={this.handleWheel}>
          {body}
        </div>
      )
    }
  }
}

const mapStateToProps = state => ({
  settingsIsReady: settingsIsReady(state),
  notifyIsReady: notifyIsReady(state),
  translationsReady: getTranslationsReady(state),
  hasError: hasError(state),
  errorMessage: getErrorMessage(state),
  toast: getToast(state),
  polyglot: getP(state),
  availableLanguages: getAvailableLanguages(state),
  globalMessage: getGlobalMessage(state),
  forceLogin: selectForceLogin(state),
  isLoggedIn: isLoggedIn(state),
  tagManagerId: getTagManagerId(state),
  defaultRoute: getDefaultRoute(state)
})

const mapDispatchToProps = {
  setLanguage,
  initApp,
  dismissError,
  deviceNotify
}

export default connect(mapStateToProps, mapDispatchToProps)(DefaultApp)
